<template>
	<v-dialog
		v-model="showDialog"
		max-width="960px"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<v-card :tile="$vuetify.breakpoint.xs">
			<v-toolbar dark color="primary" dense flat>
				<v-toolbar-title style="color: white">最終検査結果報告</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon dark @click="showDialog = false" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-sheet class="pa-2" elevation="0">
				<v-row v-if="isApprovalRequired" class="mt-2" no-gutters justify="end">
					<ConfirmationDialog
						:dialog.sync="showRequestApprovalDialog"
						:confirm_options="approval_required_confirm_options"
						requireBtn
						@accept="requestApprovalFinalInspection"
					>
						<template v-slot:open-btn-content> 承認を依頼する </template>
					</ConfirmationDialog>
				</v-row>

				<template
					v-if="
						authority_flag && order_individual_info.final_inspection_status === 'approval_pending'
					"
				>
					<v-row class="mt-4" no-gutters justify="center">
						<v-alert max-width="600px" outlined type="warning" color="red" prominent>
							<p class="text-body-2 mx-sm-4 mx-2">
								この最終検査は、<strong>承認待ち</strong>となっています。
								<br />
								承認、または、差し戻しを行ってください。
							</p>
							<v-row class="mt-4" no-gutters justify="space-around">
								<ConfirmationDialog
									v-if="authority_flag"
									:dialog.sync="approvalDialog"
									:confirm_options="confirm_options"
									requireBtn
									@accept="approveFinalInspection"
								>
									<template v-slot:open-btn-content>
										<v-icon class="mr-1"> mdi-check </v-icon>
										承認する
									</template>
								</ConfirmationDialog>
								<v-menu v-model="menu" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<v-btn color="red" dark v-bind="attrs" v-on="on">
											<v-icon class="mr-1">mdi-alert-circle-outline</v-icon>
											差し戻し
										</v-btn>
									</template>

									<v-card width="400px" class="top-border-menu top-border-menu--red">
										<v-form ref="form" v-model="valid" class="pa-2">
											<v-textarea
												class="mt-4"
												v-model="reason"
												label="差し戻し理由"
												:rules="[rules.required]"
												hint="差し戻し理由は依頼者へメッセージとして送信されます。"
												outlined
												required
											/>
										</v-form>
										<v-card-actions class="ma-0">
											<v-row no-gutters justify="space-around">
												<v-btn
													color="red"
													:dark="valid"
													@click="rejectFinalInspection()"
													:disabled="!valid"
												>
													<v-icon class="mr-1">mdi-alert-circle-outline</v-icon>
													差し戻し
												</v-btn>
												<v-btn text @click="menu = false"> キャンセル </v-btn>
											</v-row>
										</v-card-actions>
									</v-card>
								</v-menu>
							</v-row>
						</v-alert>
					</v-row>
				</template>

				<table width="100%" class="mt-4 show-dialog-table">
					<tbody>
						<ShowDetailRecord
							:columnName="'承認状態'"
							:value="order_individual_info.final_inspection_status_i18n"
						/>
						<ShowDetailRecord :columnName="'製造番号'">
							<template v-slot:value>
								<a @click="openOrderIndividualInfoDialog(order_individual_info)">
									{{ common.productNumberByOrderIndividualInfo(order_individual_info) }}
								</a>
							</template>
						</ShowDetailRecord>
						<ShowDetailRecord
							:columnName="'製品名'"
							:value="common.productNameWithSubName(order_individual_info.ordered_product)"
						/>
						<ShowDetailRecord :columnName="'数量'" :value="order_individual_info.quantity" />
					</tbody>
				</table>
				<table width="100%" class="mt-2 show-dialog-table">
					<tbody>
						<tr>
							<th class="font-weight-bold" width="100%">関連資料一覧</th>
						</tr>
						<template v-if="attached_files.length">
							<tr v-for="file in attached_files" :key="file.id">
								<td width="100%">
									<a class="d-flex" :href="file.url" target="_blank">
										{{ file.name }}
									</a>
								</td>
							</tr> </template
						><template v-else>
							<tr>
								<td width="100%">
									<p>関連資料がありません。</p>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</v-sheet>
		</v-card>
		<OrderIndividualInfoShowDialog :id="selectedInfoId" :dialog.sync="infoDialog" />
	</v-dialog>
</template>

<script>
import axios from "axios"
import errorHandling from "../../../helper/errorHandling"
import authenticate from "../../../helper/authenticate"
import common from "../../../helper/common"
import rules from "../../../helper/rules"
import ShowDetailRecord from "../../shared/ShowDetailRecord.vue"
import ConfirmationDialog from "../../shared/ConfirmationDialog.vue"
import OrderIndividualInfoShowDialog from "../../order_individual_info/components/OrderIndividualInfoShowDialog.vue"

export default {
	props: {
		dialog: {
			type: Boolean,
			required: false,
			default: false,
		},
		id: {
			type: null,
			required: false,
		},
		authority_flag: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	components: {
		ShowDetailRecord,
		ConfirmationDialog,
		OrderIndividualInfoShowDialog,
	},
	data() {
		return {
			order_individual_info: {
				ordered_product: {
					product: {},
				},
			},
			attached_files: [],
			confirm_options: {
				title: "最終検査承認",
				description: "本当に承認しますか？",
				accept_text: "承認",
				cancel_text: "キャンセル",
			},
			approval_required_confirm_options: {
				title: "承認依頼申請",
				description: "承認を依頼してよろしいですか？",
				accept_text: "依頼する",
				cancel_text: "キャンセル",
			},
			selectedInfoId: null,
			infoDialog: false,
			approvalDialog: false,
			valid: false,
			menu: false,
			reason: null,
			common,
			rules,
			showRequestApprovalDialog: false,
		}
	},
	methods: {
		fetchOrderIndividualInfo() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/final_inspections/${this.id}`
			axios
				.get(url, { headers })
				.then((res) => {
					this.order_individual_info = res.data.order_individual_info
					this.attached_files = res.data.attached_files
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		approveFinalInspection() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/final_inspections/${this.id}/approvals`
			axios
				.post(url, {}, { headers })
				.then((res) => {
					this.order_individual_info = Object.assign({}, this.order_individual_info, {
						final_inspection_status: res.data.final_inspection_status,
						final_inspection_status_i18n: res.data.final_inspection_status_i18n,
					})
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "承認しました。",
					})
					this.$emit("update-final-inspection", res.data)
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		requestApprovalFinalInspection() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/final_inspections/${this.id}`
			axios
				.put(url, {}, { headers })
				.then((res) => {
					this.order_individual_info = Object.assign({}, this.order_individual_info, {
						final_inspection_status: res.data.final_inspection_status,
						final_inspection_status_i18n: res.data.final_inspection_status_i18n,
					})
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "承認依頼をしました。",
					})
					this.$emit("update-final-inspection", res.data)
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		rejectFinalInspection() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/final_inspections/${this.id}/approvals`
			axios
				.delete(url, { headers })
				.then((res) => {
					this.order_individual_info = Object.assign({}, this.order_individual_info, {
						final_inspection_status: res.data.final_inspection_status,
						final_inspection_status_i18n: res.data.final_inspection_status_i18n,
					})
					this.$store.dispatch("setFlash", {
						type: "alert",
						text: "差し戻しをしました。",
					})
					this.$emit("update-final-inspection", res.data)
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		openOrderIndividualInfoDialog(order_individual_info) {
			this.selectedInfoId = order_individual_info.id
			this.infoDialog = true
		},
	},
	computed: {
		showDialog: {
			get() {
				return this.dialog
			},
			set(newVal) {
				return this.$emit("update:dialog", newVal)
			},
		},
		isApprovalRequired() {
			return (
				this.order_individual_info.final_inspection_status === "pending" ||
				this.order_individual_info.final_inspection_status === "rejected"
			)
		},
	},
	watch: {
		id(newVal, oldVal) {
			if (newVal == oldVal) return
			this.fetchOrderIndividualInfo(newVal)
		},
		deep: true,
	},
}
</script>
<style lang="scss" scoped>
table {
	th {
		font-weight: bold;
		width: 200px;
		min-width: 200px;
		padding: 4px 8px;
		// background-color: lightgrey;
		background-color: whitesmoke;
		border-bottom: 1px solid white;
		border-right: 1px solid white;
	}
	th.result-th {
		background-color: whitesmoke;
	}
	td {
		width: 200px;
		min-width: 200px;
		padding: 4px;
		border: 1px solid whitesmoke;
		white-space: normal;
		p {
			white-space: pre-wrap;
		}
		p.no-wrap {
			white-space: nowrap;
		}
	}
}
</style>
